import React, {
  memo,
  useMemo,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { DataContext } from "../../../providers/DataProvider";
import useDecimateWorker from "../../../utilities/useDecimateWorker";
import { useContainerSize } from "../../../utilities/useContainerSize";
import { MiniGraphOptions } from "../config/MiniGraphOptions";
import "../../../styles/MiniGraphGrid.css";
import "chartjs-adapter-date-fns"; // date-fns adapter for Chart.js necessary for decimation
import { Line } from "react-chartjs-2";
import {
  handleAllSelectorClick,
  handleRowSelectorClick,
  handleColumnSelectorClick,
} from "../../../utilities/Handlers";
import {
  useSelectionContainer,
  Box,
  boxesIntersect,
} from "@air/react-drag-to-select";
import DotWaveLoader from "../../../assets/animations/DotWaveLoader";
import * as d3 from "d3";

const INDICATOR_COLORS = [
  "rgb(75, 192, 192)",
  "rgb(255, 99, 132)",
  "rgb(54, 162, 235)",
  "rgb(255, 206, 86)",
  "rgb(153, 102, 255)",
  "rgb(255, 159, 64)",
];

const MINI_LINE_BASE_STYLE = {
  zIndex: 10,
  width: "100%",
  height: "100%",
};

// Memoized per-well chart. Re-renders only when this well's own selection
// state, indicators, options, dimensions, or filtered/raw toggle change —
// NOT when an unrelated well is clicked, which previously forced all
// 96/384 Chart.js instances to redraw.
const MiniLine = memo(function MiniLine({
  well,
  isSelected,
  showFiltered,
  minigraphOptions,
  cellWidth,
  cellHeight,
  onWellClick,
}) {
  const data = useMemo(
    () => ({
      datasets: well.indicators
        .filter((indicator) => indicator.isDisplayed)
        .map((indicator, indIndex) => {
          // Default (showFiltered=false) reads the pre-decimated
          // miniRawPoints (~80 points/well) — Chart.js holds 96×80 across
          // the whole grid instead of 96×N raw, which is what made huge
          // files load-OOM. showFiltered=true builds {x,y}[] for this
          // well's filtered data on demand; toggling it on a 200MB+ file
          // across all 384 wells is expensive (~700MB transient), so
          // prefer FilteredGraph for bulk filtered viewing on big data.
          const filteredPoints =
            showFiltered &&
            typeof indicator.materializeFilteredData === "function"
              ? indicator.materializeFilteredData()
              : indicator.filteredData;
          const rawPoints =
            indicator.miniRawPoints || indicator.rawData;
          return {
            label: `${well.label} - Indicator ${indIndex + 1}`,
            data: showFiltered ? filteredPoints : rawPoints,
            fill: false,
            borderColor: INDICATOR_COLORS[indIndex % INDICATOR_COLORS.length],
            tension: 1,
            hidden: !indicator.isDisplayed,
          };
        }),
    }),
    [well, showFiltered]
  );

  const style = useMemo(
    () => ({
      ...MINI_LINE_BASE_STYLE,
      maxWidth: cellWidth,
      maxHeight: cellHeight,
      ...(isSelected ? { border: "solid 0.1em yellow" } : {}),
    }),
    [isSelected, cellWidth, cellHeight]
  );

  const handleClick = useCallback(() => onWellClick(well.id), [onWellClick, well.id]);

  return (
    <Line
      type="line"
      id="minigraphCanvas"
      className="minigraph-and-controls__minigraph-canvas"
      data-well-id={well.id}
      style={style}
      data={data}
      options={minigraphOptions}
      onClick={handleClick}
    />
  );
});

export const MiniGraphGrid = ({
  extractedIndicatorTimes,
  smallCanvasWidth,
  smallCanvasHeight,
  largeCanvasWidth,
  largeCanvasHeight,
  columnLabels,
  rowLabels,
}) => {
  const {
    project,
    wellArrays,
    showFiltered,
    selectedWellArray,
    setSelectedWellArray, // <-- Now available from context
    handleSelectWell,
    handleDeselectWell,
    handleClearSelectedWells,
    maxPoints, // <-- Get maxPoints from context
    // rowLabels,
    // columnLabels,
  } = useContext(DataContext);

  const { decimate } = useDecimateWorker();
  // const [decimatedWellArrays, setDecimatedWellArrays] = useState([]);

  const decimationSamples = 100;

  // // State to track if rendering is complete
  const [isRenderingComplete, setIsRenderingComplete] = useState(false);

  const numRows = rowLabels.length;
  const numColumns = columnLabels.length;

  // Track the actual container size via ResizeObserver — the previous
  // `window.innerWidth / 2.3` heuristic mismatched the real grid cell
  // (the symmetric four-quadrant layout makes each quadrant ~6/14 of
  // the available width minus column gaps + panel border), causing the
  // last column of wells to fall off the right edge.
  const [containerRef, containerSize] = useContainerSize();
  const availableWidth = containerSize.width || 1;
  const availableHeight = containerSize.height || 1;

  // Fixed button dimensions
  const buttonHeight = 40; // Height of row buttons
  const buttonWidth = 80; // Width of column buttons

  // Parent container dimensions (e.g., from the parent layout or window)
  const parentWidth = availableWidth - buttonWidth;
  const parentHeight = availableHeight - buttonHeight;

  // Dynamically calculate grid dimensions
  const gridWidth = availableWidth - buttonWidth / 4; // Deduct column button area
  const gridHeight = availableHeight - buttonHeight / 2; // Deduct row + "all" button area

  const cellWidth = gridWidth / numColumns;
  const cellHeight = gridHeight / numRows;
  // Dynamically calculate available grid space
  const availableGridWidth = parentWidth - buttonWidth; // Remaining width for the grid
  const availableGridHeight = parentHeight - buttonHeight; // Remaining height for the grid

  useEffect(() => {
    if (wellArrays.length > 0) {
      // Introduce a small delay before setting isRenderingComplete
      const timeout = setTimeout(() => {
        setIsRenderingComplete(true);
      }, 1500); // keep loader for at least 1.5 sec

      return () => clearTimeout(timeout); // Cleanup timeout on component unmount
    }
  }, [wellArrays]);

  // decimation useEffect
  // useEffect(() => {
  //   async function decimateAllWells() {
  //     const decimated = await Promise.all(
  //       wellArrays.map(async (well) => ({
  //         ...well,
  //         indicators: await Promise.all(
  //           well.indicators.map(async (indicator) => {
  //             const dataToDecimate = showFiltered
  //               ? indicator.filteredData
  //               : indicator.rawData;
  //             const decimatedData = await decimate(
  //               dataToDecimate,
  //               decimationSamples
  //             ); // Adjust sample count as needed
  //             return {
  //               ...indicator,
  //               decimatedData,
  //             };
  //           })
  //         ),
  //       }))
  //     );
  //     setDecimatedWellArrays(decimated);
  //   }
  //   if (wellArrays.length > 0) decimateAllWells();
  // }, [wellArrays, showFiltered, decimate]);

  // console.log(decimatedWellArrays);
  // Refs used for the drag-to-select box
  const gridRef = useRef(null); // Ref to the grid container
  const selectableItems = useRef([]); // Store selectable items for drag selection
  const selectedIndexes = useRef([]); // Store selected indexes during drag

  // handles y-scales for proper rendering
  const yValues = wellArrays.flatMap((well) =>
    showFiltered
      ? well.indicators?.filteredData?.map((point) => point.y) || []
      : well.indicators?.rawData?.map((point) => point.y) || []
  );

  const handleWellClick = useCallback(
    (wellId) => {
      const isSelected = selectedWellArray.some((w) => w.id === wellId);
      if (isSelected) {
        handleDeselectWell(wellId);
      } else {
        const well = wellArrays.find((w) => w.id === wellId);
        handleSelectWell(well);
      }
    },
    [selectedWellArray, wellArrays, handleSelectWell, handleDeselectWell]
  );

  // O(1) selection lookup; rebuilt only when the selection set changes.
  const selectedIdSet = useMemo(() => {
    const s = new Set();
    if (selectedWellArray) {
      for (let i = 0; i < selectedWellArray.length; i++) s.add(selectedWellArray[i].id);
    }
    return s;
  }, [selectedWellArray]);

  // Handle selection change to track selected wells during drag
  const handleSelectionChange = (box) => {
    // console.log("handle selection change");
    // Adjust box coordinates for scrolling
    const scrollAwareBox = {
      ...box,
      top: box.top + window.scrollY, // Adjust for vertical scroll
      left: box.left + window.scrollX, // Adjust for horizontal scroll
    };

    const newSelectedIndexes = []; // Array to store new selected indexes

    // Check which selectable items intersect with the selection box
    selectableItems.current.forEach((item) => {
      if (boxesIntersect(scrollAwareBox, item)) {
        newSelectedIndexes.push(item.index); // Add the index of the intersecting item
      }
    });

    // Store the newly selected indexes in the ref for later use
    selectedIndexes.current = newSelectedIndexes;
  };

  // Handle selection end to finalize selected wells
  // const handleSelectionEnd = () => {
  //   const wellsToSelect = []; // Array to keep track of wells to select
  //   const wellsToDeselect = []; // Array to keep track of wells to deselect
  //
  //   // Loop through selected indexes to finalize selections
  //   selectedIndexes.current.forEach((index) => {
  //     const well = wellArrays[index]; // Get the well using the index
  //     if (well) {
  //       if (!selectedWellArray.includes(well)) {
  //         wellsToSelect.push(well); // Mark well to select if not already selected
  //       } else {
  //         wellsToDeselect.push(well); // Mark well to deselect if already selected
  //       }
  //     }
  //   });
  //
  //   // Update selectedWellArray based on the wellsToSelect and wellsToDeselect
  //   wellsToSelect.forEach((well) => handleSelectWell(well)); // Select the wells
  //   wellsToDeselect.forEach((well) => handleDeselectWell(well.id)); // Deselect the wells
  //
  //   // Clear the selectedIndexes ref for future selections
  //   selectedIndexes.current = [];
  // };
  const handleSelectionEnd = () => {
    // Get the wells in the drag area
    const dragSelectedWells = wellArrays.filter((well, idx) =>
      selectedIndexes.current.includes(idx)
    );
    // Create a Set of currently selected well IDs for fast lookup
    const selectedIds = new Set(selectedWellArray.map((w) => w.id));
    // Toggle selection: if in drag area and already selected, remove; if not, add
    let newSelection = [...selectedWellArray];
    dragSelectedWells.forEach((well) => {
      if (selectedIds.has(well.id)) {
        newSelection = newSelection.filter((w) => w.id !== well.id); // Deselect
      } else {
        newSelection.push(well); // Select
      }
    });
    setSelectedWellArray(newSelection);
    selectedIndexes.current = [];
  };

  // NEW
  useEffect(() => {
    // console.log("Selectable items:", selectableItems.current);
  }, [selectableItems]);

  const updateSelectableItems = () => {
    selectableItems.current = [];
    if (gridRef.current) {
      Array.from(gridRef.current.children).forEach((child, index) => {
        const { left, top, width, height } = child.getBoundingClientRect();
        selectableItems.current.push({ left, top, width, height, index }); // Add index to track wells
      });
    }
  };
  // NEW ^

  // const updateSelectableItems = () => {
  //   selectableItems.current = []; // Reset selectable items
  //   if (gridRef.current) {
  //     console.log("updatedSelectableItems: ", selectableItems);
  //     const gridChildren = Array.from(
  //       gridRef.current.querySelectorAll(
  //         ".minigraph-and-controls__minigraph-canvas"
  //       )
  //     );
  //     gridChildren.forEach((child) => {
  //       const { left, top, width, height } = child.getBoundingClientRect(); // Get bounding rect
  //       selectableItems.current.push({ left, top, width, height });
  //     });
  //   }
  // };

  useEffect(() => {
    if (isRenderingComplete && gridRef.current) {
      updateSelectableItems(); // Ensure selectable items are recalculated after rendering
    }
  }, [isRenderingComplete, wellArrays]);

  // Capture the bounding boxes of wells
  useEffect(() => {
    // console.log("update", selectableItems);
    updateSelectableItems(); // Initial update of selectable items
    window.addEventListener("resize", updateSelectableItems); // Re-capture on window resize

    return () => window.removeEventListener("resize", updateSelectableItems); // Cleanup event listener on unmount
  }, [wellArrays]); // Run effect when wellArrays changes

  // controls whether useSelectionContainer fires or not
  // const shouldStartSelecting = (target) => {
  //   // Check if the click originated from the grid container
  //   if (gridRef.current && gridRef.current.contains(target)) {
  //     return true; // Allow selection to start
  //   }
  //   return false; // Prevent selection from starting
  // };
  const shouldStartSelecting = (target) => {
    // Ensure the target is inside the gridRef
    return gridRef.current && gridRef.current.contains(target);
  };

  // Setup drag selection container
  const { DragSelection } = useSelectionContainer({
    onSelectionChange: handleSelectionChange,
    onSelectionEnd: handleSelectionEnd, // Call handleSelectionEnd when dragging ends
    selectionProps: {
      style: {
        border: "0.2em solid yellow", // Style for the selection box
        borderRadius: 0, // Rounded corners for the box
        backgroundColor: "rgb(75, 192, 192)", // Background color for the selection box
        opacity: 0.4, // Opacity for the selection box
        zIndex: 99999,
      },
    },
    isEnabled: true,
    shouldStartSelecting, // condition to control selection initializing
  });

  const minigraphOptions = useMemo(() => {
    return MiniGraphOptions(
      extractedIndicatorTimes,
      wellArrays,
      yValues,
      showFiltered,
      // decimatedWellArrays,
      maxPoints // <-- pass from context
    );
  }, [
    // extractedIndicatorTimes,
    wellArrays,
    // yValues,
    showFiltered,
    maxPoints,
  ]);

  // indicatorColors hoisted to module scope as INDICATOR_COLORS so MiniLine
  // sees a stable reference and React.memo can short-circuit re-renders.

  // Helper: in-thread decimation (downsample)
  function downsample(data, samples = 50) {
    if (!Array.isArray(data) || data.length <= samples) return data;
    const step = data.length / samples;
    const result = [];
    for (let i = 0; i < samples; i++) {
      result.push(data[Math.floor(i * step)]);
    }
    return result;
  }

  return (
    <div className="container" ref={containerRef}>
      {isRenderingComplete ? (
        <div
          className="minigraph-container"
          style={{
            display: "grid",
            /* The first column hosts the row-selector buttons, which are
             * `width: buttonWidth/4` (20px). Pre-restyle the template
             * was `${buttonWidth} 10fr` (no unit) which CSS interpreted
             * as `auto 10fr`, so the column auto-sized to its 20px
             * content. We make that explicit here. Same logic for the
             * top row and the half-height column buttons.
             */
            gridTemplateColumns: `${buttonWidth / 4}px 10fr`,
            gridTemplateRows: `${buttonHeight / 2}px 10fr`,
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* "All" button (Top-left cell) */}
          <div
            className="minigraph-all-button-container"
            style={{
              gridRow: "1 / 2",
              gridColumn: "1 / 2",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: buttonWidth / 4,
            }}
          >
            <button
              id="allButton"
              style={{
                width: buttonWidth / 4,
                height: buttonHeight / 2,
                alignItems: "center",
                padding: 0,
                fontWeight: "bold",
              }}
              onClick={() =>
                handleAllSelectorClick(
                  wellArrays,
                  selectedWellArray,
                  handleSelectWell,
                  handleClearSelectedWells
                )
              }
            >
              *
            </button>
          </div>

          {/* Column selection buttons (Top-right nested grid) */}
          <div
            className="minigraph-column-buttons-container"
            style={{
              gridRow: "1 / 2",
              gridColumn: "2 / 3",
              display: "grid",
              gridTemplateColumns: `repeat(${numColumns}, ${cellWidth}px)`,
              // justifyContent: "start",
            }}
          >
            {columnLabels.map((columnLabel) => (
              <button
                className="minigraph-and-controls__column-button"
                key={columnLabel}
                style={{
                  width: cellWidth, // Match canvas width
                  height: buttonHeight / 2, // Half canvas height
                }}
                onClick={() =>
                  handleColumnSelectorClick(
                    columnLabel,
                    wellArrays,
                    selectedWellArray,
                    handleSelectWell,
                    handleDeselectWell
                  )
                }
              >
                {columnLabel}
              </button>
            ))}
          </div>

          {/* Row selection buttons (Bottom-left column) */}
          <div
            className="minigraph-row-buttons-container"
            style={{
              gridRow: "2 / 3",
              gridColumn: "1 / 2",
              display: "grid",
              gridTemplateRows: `repeat(${numRows}, ${cellHeight}px)`,
              justifyContent: "center",
              maxWidth: buttonWidth / 4,
            }}
          >
            {rowLabels.map((rowLabel) => (
              <button
                key={rowLabel}
                style={{
                  width: buttonWidth / 4, // Match the 'all' button width
                  height: cellHeight, // Match canvas height
                  textAlign: "center",
                  padding: 0,
                }}
                onClick={() =>
                  handleRowSelectorClick(
                    rowLabel,
                    wellArrays,
                    selectedWellArray,
                    handleSelectWell,
                    handleDeselectWell
                  )
                }
              >
                {rowLabel}
              </button>
            ))}
          </div>

          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              // position: "relative",
              width: "100%",
              height: "100%",
              pointerEvents: "none", // Prevent this container from interfering with clicks
              zIndex: 99999, // prevents seleciton box from being drawn behind canvases
            }}
          >
            <DragSelection
              selectableTargets={".minigraph-and-controls__minigraph-canvas"}
              onSelectionChange={handleSelectionChange}
              selectionProps={{
                // boundingElement: ".minigraph-and-controls__minigraph-grid", // Limits selection to grid bounds
                boundingElement: ".minigraph-container", // Limits selection to grid bounds
              }}
            />
          </div>
          {/* Minigraph canvases (Bottom-right nested grid) */}
          <div
            className="minigraph-canvas-grid"
            ref={gridRef}
            style={{
              gridRow: "2 / 3",
              gridColumn: "2 / 3",
              display: "grid",
              gridTemplateColumns: `repeat(${numColumns}, ${cellWidth}px)`,
              gridTemplateRows: `repeat(${numRows}, ${cellHeight}px)`,
              // width: gridWidth,
              // height: gridHeight,
              // width: availableGridWidth,
              // height: availableGridHeight,
              gap: 0,
            }}
          >
            {/* {wellArrays?.length > 0 &&
              wellArrays.map((well) => ( */}
            {wellArrays?.length > 0 &&
              wellArrays.map((well) => (
                <MiniLine
                  key={well.id}
                  well={well}
                  isSelected={selectedIdSet.has(well.id)}
                  showFiltered={showFiltered}
                  minigraphOptions={minigraphOptions}
                  cellWidth={cellWidth}
                  cellHeight={cellHeight}
                  onWellClick={handleWellClick}
                />
              ))}
          </div>
        </div>
      ) : (
        <DotWaveLoader className="dotwave-loader" />
      )}
    </div>
  );
};

export default memo(MiniGraphGrid);

// import React, {
//   memo,
//   useMemo,
//   useContext,
//   useState,
//   useRef,
//   useEffect,
// } from "react";
// import { DataContext } from "../../../providers/DataProvider";
// import { MiniGraphOptions } from "../config/MiniGraphOptions";
// import "../../../styles/MiniGraphGrid.css";
// import "chartjs-adapter-date-fns"; // date-fns adapter for Chart.js necessary for decimation
// import { Line } from "react-chartjs-2";
// import {
//   handleAllSelectorClick,
//   handleRowSelectorClick,
//   handleColumnSelectorClick,
// } from "../../../utilities/Handlers";
// import {
//   useSelectionContainer,
//   Box,
//   boxesIntersect,
// } from "@air/react-drag-to-select";
// import DotWaveLoader from "../../../assets/animations/DotWaveLoader";
// import * as d3 from "d3";

// export const MiniGraphGrid = ({ rowLabels, columnLabels, metricIndicator }) => {
//   const {
//     project,
//     wellArrays,
//     showFiltered,
//     selectedWellArray,
//     handleSelectWell,
//     handleDeselectWell,
//     handleClearSelectedWells,
//     analysisData,
//     extractedIndicatorTimes,
//   } = useContext(DataContext);

//   const [isRenderingComplete, setIsRenderingComplete] = useState(false);

//   const numRows = rowLabels.length;
//   const numColumns = columnLabels.length;

//   // State for grid and cell dimensions
//   const [gridWidth, setGridWidth] = useState(window.innerWidth / 2.3);
//   const [gridHeight, setGridHeight] = useState(window.innerHeight / 2.3);

//   const cellWidth = gridWidth / numColumns;
//   const cellHeight = gridHeight / numRows;

//   // Resize handler for dynamic grid resizing
//   const handleResize = () => {
//     setGridWidth(window.innerWidth / 2.3);
//     setGridHeight(window.innerHeight / 2.3);
//   };

//   // Add resize event listener
//   useEffect(() => {
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     if (wellArrays.length > 0) {
//       // Introduce a small delay before setting isRenderingComplete
//       const timeout = setTimeout(() => {
//         setIsRenderingComplete(true);
//       }, 1500); // keep loader for at least 1.5 sec

//       return () => clearTimeout(timeout); // Cleanup timeout on component unmount
//     }
//   }, [wellArrays]);

//   //   // Refs used for the drag-to-select box
//   const gridRef = useRef(null); // Ref to the grid container
//   const selectableItems = useRef([]); // Store selectable items for drag selection
//   const selectedIndexes = useRef([]); // Store selected indexes during drag

//   // handles y-scales for proper rendering
//   const yValues = wellArrays.flatMap((well) =>
//     showFiltered
//       ? well.indicators?.filteredData?.map((point) => point.y) || []
//       : well.indicators?.rawData?.map((point) => point.y) || []
//   );

//   const handleWellClick = (wellId) => {
//     const isSelected = selectedWellArray.some((well) => well.id === wellId);

//     if (isSelected) {
//       handleDeselectWell(wellId); // Deselect by ID if already selected
//     } else {
//       const well = wellArrays.find((well) => well.id === wellId);
//       handleSelectWell(well); // Select by finding the well in wellArrays
//     }
//   };

//   // Handle selection change to track selected wells during drag
//   const handleSelectionChange = (box) => {
//     const scrollAwareBox = {
//       ...box,
//       top: box.top + window.scrollY,
//       left: box.left + window.scrollX,
//     };

//     const newSelectedIndexes = [];

//     selectableItems.current.forEach((item) => {
//       if (boxesIntersect(scrollAwareBox, item)) {
//         newSelectedIndexes.push(item.index); // Use the stored index
//       }
//     });

//     selectedIndexes.current = newSelectedIndexes;
//   };

//   // Handle selection end to finalize selected wells
//   // const handleSelectionEnd = () => {
//   //   const wellsToSelect = []; // Array to keep track of wells to select
//   //   const wellsToDeselect = []; // Array to keep track of wells to deselect
//
//   //   // Loop through selected indexes to finalize selections
//   //   selectedIndexes.current.forEach((index) => {
//   //     const well = wellArrays[index]; // Get the well using the index
//   //     if (well) {
//   //       if (!selectedWellArray.includes(well)) {
//   //         wellsToSelect.push(well); // Mark well to select if not already selected
//   //       } else {
//   //         wellsToDeselect.push(well); // Mark well to deselect if already selected
//   //       }
//   //     }
//   //   });
//
//   //   // Update selectedWellArray based on the wellsToSelect and wellsToDeselect
//   //   wellsToSelect.forEach((well) => handleSelectWell(well)); // Select the wells
//   //   wellsToDeselect.forEach((well) => handleDeselectWell(well.id)); // Deselect the wells
//
//   //   // Clear the selectedIndexes ref for future selections
//   //   selectedIndexes.current = [];
//   // };
//   const handleSelectionEnd = () => {
//     const wellsToSelect = [];
//     const wellsToDeselect = [];

//     selectedIndexes.current.forEach((index) => {
//       const well = wellArrays[index]; // Map the index correctly
//       if (well) {
//         if (!selectedWellArray.includes(well)) {
//           wellsToSelect.push(well);
//         } else {
//           wellsToDeselect.push(well);
//         }
//       }
//     });

//     wellsToSelect.forEach((well) => handleSelectWell(well));
//     wellsToDeselect.forEach((well) => handleDeselectWell(well.id));

//     selectedIndexes.current = [];
//   };

//   useEffect(() => {
//     console.log("Selectable items:", selectableItems.current);
//   }, [selectableItems]);

//   const updateSelectableItems = () => {
//     selectableItems.current = [];
//     if (gridRef.current) {
//       Array.from(gridRef.current.children).forEach((child, index) => {
//         const { left, top, width, height } = child.getBoundingClientRect();
//         selectableItems.current.push({ left, top, width, height, index }); // Add index to track wells
//       });
//     }
//   };

//   useEffect(() => {
//     if (isRenderingComplete && gridRef.current) {
//       updateSelectableItems(); // Ensure selectable items are recalculated after rendering
//     }
//   }, [isRenderingComplete, wellArrays]);

//   // Capture the bounding boxes of wells
//   useEffect(() => {
//     updateSelectableItems(); // Initial update of selectable items
//     window.addEventListener("resize", updateSelectableItems); // Re-capture on window resize

//     return () => window.removeEventListener("resize", updateSelectableItems); // Cleanup event listener on unmount
//   }, [wellArrays]); // Run effect when wellArrays changes

//   // controls whether useSelectionContainer fires or not
//   const shouldStartSelecting = (target) => {
//     // Check if the click originated from the grid container
//     if (gridRef.current && gridRef.current.contains(target)) {
//       return true; // Allow selection to start
//     }
//     return false; // Prevent selection from starting
//   };

//   // Setup drag selection container
//   const { DragSelection } = useSelectionContainer({
//     onSelectionChange: handleSelectionChange,
//     onSelectionEnd: handleSelectionEnd, // Call handleSelectionEnd when dragging ends
//     selectionProps: {
//       style: {
//         border: "0.2em solid yellow", // Style for the selection box
//         borderRadius: 0, // Rounded corners for the box
//         backgroundColor: "rgb(75, 192, 192)", // Background color for the selection box
//         opacity: 0.4, // Opacity for the selection box
//         zIndex: 99999,
//       },
//     },
//     isEnabled: true,
//     shouldStartSelecting, // condition to control selection initializing
//   });

//   const minigraphOptions = useMemo(() => {
//     return MiniGraphOptions(
//       analysisData,
//       extractedIndicatorTimes,
//       wellArrays,
//       yValues
//     );
//   }, [
//     analysisData,
//     extractedIndicatorTimes,
//     wellArrays,
//     yValues,
//     // showFiltered,
//   ]);

//   const indicatorColors = [
//     "rgb(75, 192, 192)", // Teal
//     "rgb(255, 99, 132)", // Red
//     "rgb(54, 162, 235)", // Blue
//     "rgb(255, 206, 86)", // Yellow
//     "rgb(153, 102, 255)", // Purple
//     "rgb(255, 159, 64)", // Orange
//     // Add more colors as needed
//   ];
//   console.log(cellHeight, cellWidth);

//   return (
//     <>
//       {isRenderingComplete ? (
//         <div
//           className="minigraph-and-controls__minigraph-container"
//           // style={{ height: largeCanvasHeight, width: largeCanvasWidth }}
//         >
//           <button
//             id="allButton"
//             className="minigraph-and-controls__all-button"
//             onClick={() =>
//               handleAllSelectorClick(
//                 wellArrays,
//                 selectedWellArray,
//                 handleSelectWell,
//                 handleClearSelectedWells
//               )
//             }
//           >
//             all
//           </button>
//           <div
//             className="minigraph-and-controls__column-selectors"
//             style={{
//               width: "100%",
//               // height: smallCanvasHeight,
//             }}
//           >
//             {columnLabels.map((columnLabel) => (
//               <button
//                 id="columnButton"
//                 className="minigraph-and-controls__column-button"
//                 key={columnLabel}
//                 style={{
//                   display: "flex",
//                   width: "100%",
//                   // height: smallCanvasHeight,
//                   justifyContent: "center",
//                   justifySelf: "center",
//                 }}
//                 onClick={() =>
//                   handleColumnSelectorClick(
//                     columnLabel,
//                     wellArrays,
//                     selectedWellArray,
//                     handleSelectWell,
//                     handleDeselectWell
//                   )
//                 }
//               >
//                 {columnLabel}
//               </button>
//             ))}
//           </div>
//           <div
//             className="minigraph-and-controls__row-selectors"
//             style={{
//               width: "100%",
//               height: "100%",
//             }}
//           >
//             {rowLabels.map((rowLabel) => (
//               <button
//                 id="rowButton"
//                 className="minigraph-and-controls__row-button"
//                 key={rowLabel}
//                 style={{
//                   // width: { smallCanvasWidth },
//                   // height: { smallCanvasHeight },
//                   width: "100%",
//                   height: "100%",
//                 }}
//                 onClick={() =>
//                   handleRowSelectorClick(
//                     rowLabel,
//                     wellArrays,
//                     selectedWellArray,
//                     handleSelectWell,
//                     handleDeselectWell
//                   )
//                 }
//               >
//                 {rowLabel}
//               </button>
//             ))}
//           </div>
//           <div
//             className="minigraph-and-controls__minigraph-grid"
//             ref={gridRef} // Assign the ref to the grid container
//             style={{
//               // height: largeCanvasHeight,
//               // width: largeCanvasWidth,
//               overflow: "hidden", // Prevent the drag selector from going outside the grid
//             }}
//           >
//             {/* DragSelection is absolutely positioned and won't interfere with grid layout */}
//             <div
//               style={{
//                 position: "absolute",
//                 top: 0,
//                 left: 0,
//                 right: 0,
//                 bottom: 0,
//                 pointerEvents: "none", // Prevent this container from interfering with clicks
//                 zIndex: 200000, // prevents seleciton box from being drawn behind canvases
//               }}
//             >
//               <DragSelection
//                 selectableTargets={".minigraph-and-controls__minigraph-canvas"}
//                 onSelectionChange={handleSelectionChange}
//                 selectionProps={{
//                   // boundingElement: ".minigraph-and-controls__minigraph-grid", // Limits selection to grid bounds
//                   boundingElement:
//                     ".minigraph-and-controls__minigraph-container", // Limits selection to grid bounds
//                 }}
//               />
//             </div>
//             <div
//               style={{
//                 display: "grid",
//                 gridTemplateRows: `repeat(${numRows}, ${cellHeight}px)`,
//                 gridTemplateColumns: `repeat(${numColumns}, ${cellWidth}px)`,
//                 width: gridWidth,
//                 height: gridHeight,
//               }}
//             >
//               {wellArrays?.length > 0 &&
//                 wellArrays.map((well) => (
//                   <Line
//                     type="line"
//                     id="minigraphCanvas"
//                     className="minigraph-and-controls__minigraph-canvas"
//                     data-well-id={well.id}
//                     style={
//                       selectedWellArray?.some(
//                         (selectedWell) => selectedWell.id === well.id
//                       )
//                         ? {
//                             border: "solid 0.1em yellow",
//                             zIndex: 10,
//                             // width: "95%",
//                             // height: "95%",
//                             width: "100%",
//                             height: "100%",
//                             maxWidth: cellWidth,
//                             maxHeight: cellHeight,
//                             // width: cellWidth,
//                             // height: cellHeight,
//                             // width: `${cellWidth}px`,
//                             // height: `${cellHeight}px`,
//                           } // styling for selected wells
//                         : {
//                             // border: "solid 1px grey",

//                             zIndex: 10,
//                             // width: "95%",
//                             // height: "95%",
//                             width: "100%",
//                             height: "100%",
//                             maxWidth: cellWidth,
//                             maxHeight: cellHeight,
//                             // width: cellWidth,
//                             // height: cellHeight,
//                             // width: `${cellWidth}px`,
//                             // height: `${cellHeight}px`,
//                           } // styling for un-selected wells
//                     }
//                     key={well.id}
//                     data={{
//                       datasets: well.indicators
//                         .filter((indicator) => indicator.isDisplayed)
//                         .map((indicator, indIndex) => ({
//                           label: `${well.label} - Indicator ${indIndex + 1}`, // Label for each indicator
//                           data: showFiltered
//                             ? indicator.filteredData
//                             : indicator.rawData,
//                           fill: false,
//                           borderColor:
//                             indicatorColors[indIndex % indicatorColors.length], // Cycle colors
//                           tension: 1,
//                           hidden: !indicator.isDisplayed,
//                         })),
//                     }}
//                     options={minigraphOptions}
//                     onClick={() => handleWellClick(well.id)}
//                   />
//                 ))}
//             </div>
//           </div>
//         </div>
//       ) : (
//         <DotWaveLoader className="dotwave-loader" />
//       )}
//     </>
//   );
// };

// export default MiniGraphGrid;
